'use client';

import * as React from 'react';
import { useDebouncedCallback, useLocalStorageValue } from '@react-hookz/web';
import { useMutation } from '@tanstack/react-query';
import { IconMessageCircle02 } from 'icon/Communication/message-circle-02';
import { useTranslations } from 'next-intl';
import { api } from 'orpc/react';
import {
  Separator,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'ui';
import { LikeIcon } from './like-icon';

export type AsideActionProps = {
  showComment: boolean;
  showLike: boolean;
  postId: string;
  domain: string;
  commentCount: number;
  likeCount: number;
};

export function AsideActions({
  showComment,
  showLike,
  postId,
  domain,
  commentCount,
  likeCount,
}: AsideActionProps) {
  const [open, setOpen] = React.useState(false);
  const t = useTranslations('Blog.Post.Aside');
  const isDark = useIsDarkTheme();
  const { value: likedPosts, set: setLikedPosts } = useLocalStorageValue<
    string[]
  >('likedPosts', {
    defaultValue: [],
  });
  const liked = likedPosts?.includes(postId);
  const { mutate } = useMutation(api.blog.likePost.mutationOptions());
  const initialLikeState = React.useRef<boolean | null>(null);
  const likePost = useDebouncedCallback(
    (currentLiked: boolean) => {
      if (
        initialLikeState.current !== null &&
        currentLiked === initialLikeState.current
      ) {
        // Only update the server if the user has changed their like state
        // skip if the user rolls back to the original state
        mutate({ postId, isLiked: currentLiked });
      }
      initialLikeState.current = null;
    },
    [],
    3000,
  );
  const [_likeCount, setLikeCount] = React.useState(likeCount);
  const handleLikeClick = () => {
    const nextState = !liked;
    if (initialLikeState.current === null) {
      initialLikeState.current = nextState;
    }
    if (liked) {
      setLikedPosts(likedPosts?.filter((id) => id !== postId) || []);
      setLikeCount((prev) => prev - 1);
    } else {
      setLikedPosts([...(likedPosts || []), postId]);
      setLikeCount((prev) => prev + 1);
    }
    likePost(nextState);
  };
  return (
    <div className="bg-primary">
      <Separator />
      <div className="flex min-w-[130px] items-center gap-2 pt-7">
        <TooltipProvider delayDuration={1000}>
          {showComment && (
            <Sheet open={open} onOpenChange={setOpen}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    id="comment"
                    type="button"
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-quaternary hover:bg-secondary-hover"
                    aria-label={t('Comments')}
                    onClick={() => setOpen(true)}
                  >
                    <IconMessageCircle02 className="size-6" />
                    {commentCount > 0 ? (
                      <span className="text-sm font-medium">
                        {commentCount}
                      </span>
                    ) : null}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('Comments')}</p>
                </TooltipContent>
              </Tooltip>
              <SheetContent
                side="left"
                className="w-[400px] max-w-none px-4 pb-4 pt-10 sm:w-[540px]"
                onOpenAutoFocus={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
              >
                <SheetHeader>
                  <SheetTitle className="sr-only">{t('Comments')}</SheetTitle>
                  <SheetDescription className="sr-only">
                    {t('Comments')}
                  </SheetDescription>
                </SheetHeader>
                {isDark !== null && (
                  <ChirpyComment isDark={isDark} domain={domain} />
                )}
              </SheetContent>
            </Sheet>
          )}
          {showLike && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  id="like"
                  type="button"
                  className="group inline-flex items-center gap-1 rounded px-2 py-1 text-quaternary hover:bg-secondary-hover"
                  aria-label={t('Like')}
                  onClick={handleLikeClick}
                >
                  <LikeIcon isLiked={!!liked} />
                  {_likeCount > 0 ? (
                    <span className="text-sm font-medium">{_likeCount}</span>
                  ) : null}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('Like')}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </TooltipProvider>
      </div>
    </div>
  );
}

function ChirpyComment({
  isDark,
  domain,
}: {
  isDark: boolean;
  domain: string;
}) {
  React.useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://chirpy.dev/bootstrapper.js';
    script.id = 'chirpy-bootstrapper';
    script.setAttribute('data-chirpy-domain', domain);
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [domain]);
  return (
    <div
      data-chirpy-comment="true"
      data-chirpy-theme={isDark ? 'dark' : 'light'}
      id="chirpy-comment"
    />
  );
}

export function useIsDarkTheme() {
  const [isDark, setIsDark] = React.useState<boolean | null>(null);
  React.useEffect(() => {
    setIsDark(document.documentElement.classList.contains('dark'));
  }, []);
  return isDark;
}
